import React from "react";
import { Grid, Typography, TextField, Box, Tooltip, IconButton } from "@mui/material";
import { Info as InfoIcon } from "@mui/icons-material";
import { validateExpiry } from './cardValidation';
import { detectCardType } from './cardValidation';

const CardFormFields = ({ 
  cardData, 
  errors, 
  onInputChange, 
  isCvvFocused, 
  setIsCvvFocused 
}) => {
  return (
    <Grid container spacing={3}>
      {/* CPF/CNPJ/NIF */}
      <Grid item xs={12} md={6}>
        <Typography variant="body2" fontWeight="bold" gutterBottom>
          CPF/CNPJ/NIF
        </Typography>
        <TextField
          fullWidth
          placeholder="000.000.000-00"
          value={cardData.registry_code}
          onChange={(e) => onInputChange('registry_code', e.target.value)}
          error={!!errors.registry_code}
          helperText={errors.registry_code}
          inputProps={{ maxLength: 18 }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: '#6B46C1',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#6B46C1',
              },
            },
          }}
        />
      </Grid>

      {/* Nome do Titular */}
      <Grid item xs={12} md={6}>
        <Typography variant="body2" fontWeight="bold" gutterBottom>
          NOME DO TITULAR
        </Typography>
        <TextField
          fullWidth
          placeholder="João Silva"
          value={cardData.card_name}
          onChange={(e) => onInputChange('card_name', e.target.value)}
          error={!!errors.card_name}
          helperText={errors.card_name || (
            cardData.card_name && 
            cardData.card_name.trim().length >= 2 && 
            /^[a-zA-ZÀ-ÿ\s]+$/.test(cardData.card_name.trim()) &&
            cardData.card_name.trim().split(' ').filter(word => word.length > 0).length >= 2
            ? "✓ Nome válido" : ""
          )}
          sx={{
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: '#6B46C1',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#6B46C1',
              },
              // Borda verde quando o nome está válido
              ...(cardData.card_name && 
                  cardData.card_name.trim().length >= 2 && 
                  /^[a-zA-ZÀ-ÿ\s]+$/.test(cardData.card_name.trim()) &&
                  cardData.card_name.trim().split(' ').filter(word => word.length > 0).length >= 2 &&
                  !errors.card_name && {
                '& fieldset': {
                  borderColor: '#4caf50',
                  borderWidth: '2px'
                }
              })
            },
            '& .MuiFormHelperText-root': {
              color: cardData.card_name && 
                     cardData.card_name.trim().length >= 2 && 
                     /^[a-zA-ZÀ-ÿ\s]+$/.test(cardData.card_name.trim()) &&
                     cardData.card_name.trim().split(' ').filter(word => word.length > 0).length >= 2 &&
                     !errors.card_name ? '#4caf50' : undefined
            }
          }}
        />
      </Grid>

      {/* Número do Cartão */}
      <Grid item xs={12}>
        <Typography variant="body2" fontWeight="bold" gutterBottom>
          NÚMERO DO CARTÃO
        </Typography>
        <TextField
          fullWidth
          placeholder="0000 0000 0000 0000"
          value={cardData.number}
          onChange={(e) => onInputChange('number', e.target.value)}
          error={!!errors.number}
          helperText={errors.number || (cardData.number && detectCardType(cardData.number))}
          inputProps={{ maxLength: 19 }}
          sx={{
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: '#6B46C1',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#6B46C1',
              },
            },
          }}
        />
      </Grid>

      {/* Validade */}
      <Grid item xs={12} md={6}>
        <Typography variant="body2" fontWeight="bold" gutterBottom>
          VALIDADE (MÊS / ANO)
        </Typography>
        <Box display="flex" gap={1} alignItems="center">
          <TextField
            placeholder={String(new Date().getMonth() + 1).padStart(2, '0')}
            value={cardData.expMonth}
            onChange={(e) => onInputChange('expMonth', e.target.value)}
            error={!!errors.validade}
            inputProps={{ maxLength: 2 }}
            sx={{
              width: '80px',
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: errors.validade ? '#d32f2f' : '#6B46C1',
                },
                '&.Mui-focused fieldset': {
                  borderColor: errors.validade ? '#d32f2f' : '#6B46C1',
                },
                ...(cardData.expMonth.length === 2 && parseInt(cardData.expMonth) >= 1 && parseInt(cardData.expMonth) <= 12 && !errors.validade && {
                  '& fieldset': {
                    borderColor: '#4caf50',
                    borderWidth: '2px'
                  }
                })
              },
            }}
          />
          <Typography variant="h6" sx={{ mx: 1 }}>/</Typography>
          <TextField
            placeholder={String(new Date().getFullYear() + 5)}
            value={cardData.expYear}
            onChange={(e) => onInputChange('expYear', e.target.value)}
            error={!!errors.validade}
            inputProps={{ maxLength: 4 }}
            sx={{
              width: '100px',
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: errors.validade ? '#d32f2f' : '#6B46C1',
                },
                '&.Mui-focused fieldset': {
                  borderColor: errors.validade ? '#d32f2f' : '#6B46C1',
                },
                ...(cardData.expYear.length === 4 && parseInt(cardData.expYear) >= new Date().getFullYear() && !errors.validade && {
                  '& fieldset': {
                    borderColor: '#4caf50',
                    borderWidth: '2px'
                  }
                })
              },
            }}
          />
        </Box>
        {errors.validade && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
            {errors.validade}
          </Typography>
        )}
        {!errors.validade && cardData.expMonth.length === 2 && cardData.expYear.length === 4 &&
          validateExpiry(cardData.expMonth, cardData.expYear) && (
            <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: '#4caf50' }}>
              ✓ Data de validade correta
            </Typography>
          )}
      </Grid>

      {/* CVV */}
      <Grid item xs={12} md={6}>
        <Typography variant="body2" fontWeight="bold" gutterBottom>
          CVV
          <Tooltip title="Código de segurança de 3 dígitos no verso do cartão">
            <IconButton size="small" sx={{ ml: 0.5, p: 0.5 }}>
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Typography>
        <TextField
          placeholder="000"
          value={cardData.cvv}
          onChange={(e) => onInputChange('cvv', e.target.value)}
          onFocus={() => setIsCvvFocused(true)}
          onBlur={() => setIsCvvFocused(false)}
          error={!!errors.cvv}
          helperText={errors.cvv}
          inputProps={{ maxLength: 4 }}
          sx={{
            width: '100px',
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: '#6B46C1',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#6B46C1',
              },
            },
          }}
        />
      </Grid>
    </Grid>
  );
};

export default CardFormFields;